import { landingSectionSchema, campaignPageSectionSchema, type LandingSection, type CampaignPageSection } from "@growwithai/shared";

// Bentuk data publik dari GET /api/public/landing (kontrak ws/site-api).
// Semua field nullable-toleran: API bisa mengembalikan null untuk kolom kosong.

export interface LandingCourse {
  id: string;
  slug: string;
  title: string;
  subtitle?: string | null;
  thumbnail_url?: string | null;
  level?: string | null;
  category?: string | null;
  access_type?: string | null;
  price?: number | null;
}

export interface LandingPlan {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  duration_type?: string | null;
  duration_days?: number | null;
  price: number;
  compare_at_price?: number | null;
}

export interface LandingExam {
  id: string;
  slug: string;
  title: string;
  description?: string | null;
  duration_minutes?: number | null;
}

export interface LandingData {
  courses: LandingCourse[];
  plans: LandingPlan[];
  exams: LandingExam[];
}

export const EMPTY_LANDING_DATA: LandingData = { courses: [], plans: [], exams: [] };

/**
 * Guard href buatan tenant di klien (defense-in-depth; server sudah
 * memvalidasi via safeHref zod): hanya path relatif atau http(s).
 */
export function isSafeHref(href: string): boolean {
  return /^\/(?!\/)/.test(href) || /^https?:\/\//i.test(href);
}

/**
 * Validasi config.sections mentah SATU PER SATU (bukan parse config utuh):
 * section tipe baru dari server yang belum dikenal build lama, atau section
 * korup, cukup di-skip — forward compatible sesuai rancangan. Section
 * hidden juga dibuang di sini.
 */
export function parseSections(rawSections: unknown): LandingSection[] {
  if (!Array.isArray(rawSections)) return [];
  const parsed: LandingSection[] = [];
  for (const raw of rawSections) {
    const result = landingSectionSchema.safeParse(raw);
    if (result.success && !result.data.hidden) parsed.push(result.data);
  }
  return parsed;
}

/**
 * Sama seperti parseSections, tapi untuk halaman promosi (builder per-produk) —
 * schema campaignPageSectionSchema, superset landingSectionSchema + product_offer.
 */
export function parseCampaignSections(rawSections: unknown): CampaignPageSection[] {
  if (!Array.isArray(rawSections)) return [];
  const parsed: CampaignPageSection[] = [];
  for (const raw of rawSections) {
    const result = campaignPageSectionSchema.safeParse(raw);
    if (result.success && !result.data.hidden) parsed.push(result.data);
  }
  return parsed;
}

/** Styling konten HTML kaya (hasil sanitizeLessonHtml) tanpa plugin typography. */
export const RICH_HTML_CLASS =
  "text-[15px] leading-relaxed text-gray-700 " +
  "[&_p]:mb-3 [&_p:last-child]:mb-0 " +
  "[&_h2]:mb-2 [&_h2]:mt-6 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-warm-black " +
  "[&_h3]:mb-1.5 [&_h3]:mt-5 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-warm-black " +
  "[&_h4]:mb-1.5 [&_h4]:mt-4 [&_h4]:font-semibold [&_h4]:text-warm-black " +
  "[&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 " +
  "[&_a]:text-[var(--brand-primary)] [&_a]:underline [&_a]:underline-offset-2 " +
  "[&_strong]:text-warm-black " +
  "[&_blockquote]:my-3 [&_blockquote]:border-l-4 [&_blockquote]:border-[var(--brand-primary)]/30 [&_blockquote]:pl-4 [&_blockquote]:italic " +
  "[&_img]:my-3 [&_img]:max-w-full [&_img]:rounded-xl " +
  "[&_hr]:my-6 [&_hr]:border-black/10 " +
  "[&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-black/10 [&_th]:bg-gray-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_td]:border [&_td]:border-black/10 [&_td]:px-3 [&_td]:py-2 " +
  "[&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:bg-gray-900 [&_pre]:p-4 [&_pre]:text-sm [&_pre]:text-gray-100 " +
  "[&_code]:rounded [&_code]:bg-black/[0.05] [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.9em] [&_pre_code]:bg-transparent [&_pre_code]:p-0";
